import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  ModelValidation,
  UserLoginRequest,
  UserDetails,
  VerificationRequest,
  FieldFilterRequest,
  SaveReportRequest,
  ConfigurationItem,
  Company,
  CompanyUser,
  Project,
  ProjectAssignment,
  ProjectAssignmentWorkflow,
  ProjectAssignmentWorkflowStage,
  CommunicationTemplate,
  DocumentData,
  DocumentType,
  EntityData,
  ProjectWorkRequired,
} from './inspector-api-contracts';

@Injectable({
  providedIn: 'root',
})
export class InspectorApiService {

  private readonly baseUrl = environment.apiBaseUrl;
  
  // ── Caching Configuration ──
  
  /**
   * Cache duration in milliseconds (30 seconds)
   * Validates: Requirements 10.4
   */
  private readonly CACHE_DURATION_MS = 30000;
  
  /**
   * Cache storage for API responses
   * Key format: "endpoint|params"
   * Value: { observable: Observable<ModelValidation>, timestamp: number }
   */
  private cache = new Map<string, { observable: Observable<ModelValidation>; timestamp: number }>();

  constructor(private http: HttpClient) {}

  // ── Caching & Deduplication Helpers ──
  
  /**
   * Generate cache key from endpoint and parameters
   * @param endpoint - API endpoint path
   * @param params - Optional query parameters or request body
   * @returns Cache key string
   */
  private generateCacheKey(endpoint: string, params?: any): string {
    const paramString = params ? JSON.stringify(params) : '';
    return `${endpoint}|${paramString}`;
  }
  
  /**
   * Get cached observable if exists and not expired
   * @param cacheKey - Cache key to lookup
   * @returns Cached observable or null if not found/expired
   */
  private getCachedObservable(cacheKey: string): Observable<ModelValidation> | null {
    const cached = this.cache.get(cacheKey);
    
    if (!cached) {
      return null;
    }
    
    // Check if cache has expired
    const now = Date.now();
    const isExpired = (now - cached.timestamp) > this.CACHE_DURATION_MS;
    
    if (isExpired) {
      // Remove expired cache entry
      this.cache.delete(cacheKey);
      return null;
    }
    
    return cached.observable;
  }
  
  /**
   * Cache observable with shareReplay for request deduplication
   * @param cacheKey - Cache key to store under
   * @param observable - Observable to cache
   * @returns Cached observable with shareReplay
   * 
   * Validates: Requirements 10.2, 10.4
   * 
   * Uses shareReplay(1) to:
   * - Share a single HTTP request among multiple subscribers (deduplication)
   * - Replay the last emitted value to new subscribers (caching behavior)
   * - Automatically complete and clean up when ref count reaches 0
   */
  private cacheObservable(cacheKey: string, observable: Observable<ModelValidation>): Observable<ModelValidation> {
    // Apply shareReplay for request deduplication and replay
    // shareReplay(1) ensures:
    // 1. Only one HTTP request is made even if multiple components subscribe simultaneously
    // 2. Late subscribers get the cached result without triggering a new request
    // 3. Observable completes normally after all subscribers unsubscribe
    const shared = observable.pipe(
      shareReplay({ bufferSize: 1, refCount: true })
    );
    
    // Store in cache with current timestamp
    this.cache.set(cacheKey, {
      observable: shared,
      timestamp: Date.now()
    });
    
    return shared;
  }
  
  /**
   * Execute cacheable GET request
   * @param endpoint - API endpoint path (relative to baseUrl)
   * @param params - Optional HttpParams for query string
   * @returns Observable with caching and deduplication
   * 
   * Validates: Requirements 10.2, 10.4
   */
  private getCached(endpoint: string, params?: HttpParams): Observable<ModelValidation> {
    // Generate cache key based on endpoint and parameters
    const cacheKey = this.generateCacheKey(endpoint, params?.toString());
    
    // Check if valid cached observable exists
    const cached = this.getCachedObservable(cacheKey);
    if (cached) {
      return cached;
    }
    
    // Create new HTTP request observable
    const url = `${this.baseUrl}${endpoint}`;
    const observable = this.http.get<ModelValidation>(url, { params: params || {} });
    
    // Cache and return with shareReplay for deduplication
    return this.cacheObservable(cacheKey, observable);
  }

  // ── Authentication ─────────────────────────────────────────────────────────

  /** POST /auth/users/login */
  loginUserManually(request: UserLoginRequest): Observable<ModelValidation> {
    return this.http.post<ModelValidation>(`${this.baseUrl}/auth/users/login`, request);
  }
  
  /** GET /auth/users/login */
  loginUser(): Observable<ModelValidation> {
    return this.http.get<ModelValidation>(`${this.baseUrl}/auth/users/login`, { observe: 'response' })
      .pipe(
        map((response: HttpResponse<ModelValidation>) => {
          const token = response.headers.get('Authorization');
          if (token) {
            window.sessionStorage.setItem("Authorization", token);
          }
          if (response.body) {
            window.sessionStorage.setItem("userdetails", JSON.stringify(response.body?.value))
          }
          return response.body!;
        })
      );
  }

  /** POST /auth/users/create */
  createUser(userDetails: UserDetails): Observable<ModelValidation> {
    return this.http.post<ModelValidation>(`${this.baseUrl}/auth/users/create`, userDetails);
  }

  /** PUT /auth/users/update */
  updateUser(userDetails: UserDetails): Observable<ModelValidation> {
    return this.http.put<ModelValidation>(`${this.baseUrl}/auth/users/update`, userDetails);
  }

  /** GET /auth/users */
  getUsers(): Observable<ModelValidation> {
    return this.http.get<ModelValidation>(`${this.baseUrl}/auth/users`);
  }

  /** GET /auth/users/{username} */
  getUser(username: string): Observable<ModelValidation> {
    return this.http.get<ModelValidation>(`${this.baseUrl}/auth/users/${username}`);
  }

  /** DELETE /auth/users/{id} */
  deleteUser(id: number): Observable<ModelValidation> {
    return this.http.delete<ModelValidation>(`${this.baseUrl}/auth/users/${id}`);
  }

  /** DELETE /auth/users/super/{id} */
  deleteUsers(id: string): Observable<ModelValidation> {
    return this.http.delete<ModelValidation>(`${this.baseUrl}/auth/users/super/${id}`);
  }

  // ── Auth Verification ──────────────────────────────────────────────────────

  /** PUT /auth/verification/field/trigger */
  updateUserFieldStep1(request: VerificationRequest): Observable<ModelValidation> {
    return this.http.put<ModelValidation>(`${this.baseUrl}/auth/verification/field/trigger`, request);
  }

  /** GET /auth/verification/field/confirm/{verificationCode} */
  updateUserFieldStep2(verificationCode: string): Observable<ModelValidation> {
    return this.http.get<ModelValidation>(`${this.baseUrl}/auth/verification/field/confirm/${verificationCode}`);
  }

  /** PUT /auth/verification/password/{verificationCode} */
  updateForgottenUserPassword(verificationCode: string, newPassword: string): Observable<ModelValidation> {
    return this.http.put<ModelValidation>(`${this.baseUrl}/auth/verification/password/${verificationCode}`, newPassword);
  }

  // ── Report ─────────────────────────────────────────────────────────────────

  /** POST /report/dynamic */
  getDynamicDataFromFilter(filterRequest: FieldFilterRequest): Observable<ModelValidation> {
    return this.http.post<ModelValidation>(`${this.baseUrl}/report/dynamic`, filterRequest);
  }

  /** POST /report/save */
  saveReport(request: SaveReportRequest): Observable<ModelValidation> {
    return this.http.post<ModelValidation>(`${this.baseUrl}/report/save`, request);
  }

  /** GET /report/all */
  getAllReports(): Observable<ModelValidation> {
    return this.http.get<ModelValidation>(`${this.baseUrl}/report/all`);
  }

  /** GET /report/by-name/{name}/{getCounts} */
  getReportDataByName(name: string, getCounts: boolean): Observable<ModelValidation> {
    return this.getCached(`/report/by-name/${name}/${getCounts}`);
  }

  /** GET /report/by-page/{pageName}/{getCounts} */
  getReportDataByPage(pageName: string, getCounts: boolean): Observable<ModelValidation> {
    return this.getCached(`/report/by-page/${pageName}/${getCounts}`);
  }

  // ── Configuration ──────────────────────────────────────────────────────────

  /** GET /config/all */
  getConfigurations(): Observable<ModelValidation> {
    return this.http.get<ModelValidation>(`${this.baseUrl}/config/all`);
  }

  /** GET /config/byid/{id} */
  getConfigurationById(id: number): Observable<ModelValidation> {
    return this.http.get<ModelValidation>(`${this.baseUrl}/config/byid/${id}`);
  }

  /** GET /config/bylabel/{configLabel} */
  getConfigurationByLabel(configLabel: string): Observable<ModelValidation> {
    const params = new HttpParams().set('configLabel', configLabel);
    return this.http.get<ModelValidation>(`${this.baseUrl}/config/bylabel/${configLabel}`, { params });
  }

  /** POST /config/save */
  saveConfiguration(config: ConfigurationItem): Observable<ModelValidation> {
    return this.http.post<ModelValidation>(`${this.baseUrl}/config/save`, config);
  }

  // ── Company ────────────────────────────────────────────────────────────────

  /** GET /company/user/ */
  getUserCompanies(): Observable<ModelValidation> {
    return this.http.get<ModelValidation>(`${this.baseUrl}/company/user/`);
  }

  /** GET /company/user/{userId} */
  getUserCompaniesByUserId(userId: number): Observable<ModelValidation> {
    return this.http.get<ModelValidation>(`${this.baseUrl}/company/user/${userId}`);
  }

  /** GET /company/byid/{companyId} */
  getCompany(companyId: string): Observable<ModelValidation> {
    const params = new HttpParams().set('companyId', companyId);
    return this.http.get<ModelValidation>(`${this.baseUrl}/company/byid/${companyId}`, { params });
  }

  /** GET /company/all */
  getCompanies(): Observable<ModelValidation> {
    return this.http.get<ModelValidation>(`${this.baseUrl}/company/all`);
  }

  /** GET /company/all/detailed */
  getCompanyDetails(): Observable<ModelValidation> {
    return this.http.get<ModelValidation>(`${this.baseUrl}/company/all/detailed`);
  }

  /** POST /company/save/{representative} */
  saveCompany(representative: number, company: Company): Observable<ModelValidation> {
    return this.http.post<ModelValidation>(`${this.baseUrl}/company/save/${representative}`, company);
  }

  /** POST /company/resource/save */
  saveCompanyUser(user: CompanyUser): Observable<ModelValidation> {
    const params = new HttpParams({ fromObject: { user } as any });
    return this.http.post<ModelValidation>(`${this.baseUrl}/company/resource/save`, null, { params });
  }

  // ── Project ────────────────────────────────────────────────────────────────

  /** POST /project/save */
  saveProject(project: Project): Observable<ModelValidation> {
    return this.http.post<ModelValidation>(`${this.baseUrl}/project/save`, project);
  }

  /** POST /project/save/required-work */
  saveProjectWorkRequired(request: ProjectWorkRequired): Observable<ModelValidation> {
    return this.http.post<ModelValidation>(`${this.baseUrl}/project/save/required-work`, request);
  }

  /** POST /project/resource/save */
  assignProjectResource(assignment: ProjectAssignment): Observable<ModelValidation> {
    return this.http.post<ModelValidation>(`${this.baseUrl}/project/resource/save`, assignment);
  }

  /** GET /project/user/{userId} */
  getUserProjects(userId: number): Observable<ModelValidation> {
    return this.http.get<ModelValidation>(`${this.baseUrl}/project/user/${userId}`);
  }

  /** GET /project/cc/{companyId} */
  getCompanyProjects(companyId: number): Observable<ModelValidation> {
    return this.http.get<ModelValidation>(`${this.baseUrl}/project/cc/${companyId}`);
  }

  /** GET /project/byid/{projectId} */
  getProject(projectId: string): Observable<ModelValidation> {
    return this.http.get<ModelValidation>(`${this.baseUrl}/project/byid/${projectId}`);
  }

  /** GET /project/assigned/user/{userId} */
  getProjectsAssignedToUser(userId: number): Observable<ModelValidation> {
    return this.http.get<ModelValidation>(`${this.baseUrl}/project/assigned/user/${userId}`);
  }

  /** GET /project/assigned/cc/{companyId} */
  getProjectsAssignedToCompany(companyId: number): Observable<ModelValidation> {
    return this.http.get<ModelValidation>(`${this.baseUrl}/project/assigned/cc/${companyId}`);
  }

  /** GET /project/all */
  getProjects(): Observable<ModelValidation> {
    return this.http.get<ModelValidation>(`${this.baseUrl}/project/all`);
  }

  // ── Project Assignment Workflow ────────────────────────────────────────────

  /** GET /project/resource/request */
  getAssignmentRequests(): Observable<ModelValidation> {
    return this.http.get<ModelValidation>(`${this.baseUrl}/project/resource/request`);
  }

  /** GET /project/resource/request/{projectAssignmentWorkflowId} */
  getAssignmentRequestDetails(projectAssingmentWorkflowId: number): Observable<ModelValidation> {
    return this.http.get<ModelValidation>(`${this.baseUrl}/project/resource/request/${projectAssingmentWorkflowId}`);
  }

  /** POST /project/resource/request */
  requestProjectAssignment(workflow: ProjectAssignmentWorkflow): Observable<ModelValidation> {
    return this.http.post<ModelValidation>(`${this.baseUrl}/project/resource/request`, workflow);
  }

  /** PUT /project/resource/request */
  runProjectAssignmentWorkflow(stage: ProjectAssignmentWorkflowStage): Observable<ModelValidation> {
    return this.http.put<ModelValidation>(`${this.baseUrl}/project/resource/request`, stage);
  }

  // ── Entity Data ────────────────────────────────────────────────────────────

  /** GET /entity-data/all */
  getAllEntityData(): Observable<ModelValidation> {
    return this.http.get<ModelValidation>(`${this.baseUrl}/entity-data/all`);
  }

  /** GET /entity-data/by-owner/{ownerId}/{ownerType} */
  getEntityDataByOwner(ownerId: number, ownerType: string): Observable<ModelValidation> {
    return this.http.get<ModelValidation>(`${this.baseUrl}/entity-data/by-owner/${ownerId}/${ownerType}`);
  }

  /** GET /entity-data/by-type/{entityType} */
  getEntityDataByType(entityType: string): Observable<ModelValidation> {
    return this.http.get<ModelValidation>(`${this.baseUrl}/entity-data/by-type/${entityType}`);
  }

  /** POST /entity-data/by-type-id */
  getEntityDataByTypeIds(entityTypeIds: number[]): Observable<ModelValidation> {
    return this.http.post<ModelValidation>(`${this.baseUrl}/entity-data/by-type-id`, entityTypeIds);
  }

  /** POST /entity-data/by-type-id/{ownerType} */
  getEntityDataByTypeAndOwner(ownerType: string, entityTypeIds: number[]): Observable<ModelValidation> {
    return this.http.post<ModelValidation>(`${this.baseUrl}/entity-data/by-type-id/${ownerType}`, entityTypeIds);
  }

  /** POST /entity-data/manage */
  createUpdateEntityData(entityData: EntityData): Observable<ModelValidation> {
    return this.http.post<ModelValidation>(`${this.baseUrl}/entity-data/manage`, entityData);
  }

  // ── Communication ──────────────────────────────────────────────────────────

  /** GET /communication/template */
  getTemplates(): Observable<ModelValidation> {
    return this.http.get<ModelValidation>(`${this.baseUrl}/communication/template`);
  }

  /** POST /communication/template */
  createUpdateTemplate(template: CommunicationTemplate): Observable<ModelValidation> {
    const params = new HttpParams({ fromObject: { template } as any });
    return this.http.post<ModelValidation>(`${this.baseUrl}/communication/template`, null, { params });
  }

  /** GET /communication/mail */
  sendMail(receipients: string, affectedUserId: number, message: string): Observable<ModelValidation> {
    const params = new HttpParams()
      .set('receipients', receipients)
      .set('affectedUserId', affectedUserId.toString())
      .set('message', message);
    return this.http.get<ModelValidation>(`${this.baseUrl}/communication/mail`, { params });
  }

  // ── Document ───────────────────────────────────────────────────────────────

  /** GET /document/download-pdf */
  downloadPdf(): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/document/download-pdf`, { responseType: 'blob' });
  }

  /** POST /document/upload/csv */
  uploadRecordsFromCsv(csvData: string[]): Observable<ModelValidation> {
    return this.http.post<ModelValidation>(`${this.baseUrl}/document/upload/csv`, csvData);
  }

  // Document Types

  /** GET /document/types */
  getAllDocumentTypes(): Observable<ModelValidation> {
    return this.http.get<ModelValidation>(`${this.baseUrl}/document/types`);
  }

  /** GET /document/types/{documentTypeId} */
  getDocumentTypeById(documentTypeId: number): Observable<ModelValidation> {
    return this.http.get<ModelValidation>(`${this.baseUrl}/document/types/${documentTypeId}`);
  }

  /** POST /document/types */
  createDocumentType(documentType: DocumentType): Observable<ModelValidation> {
    return this.http.post<ModelValidation>(`${this.baseUrl}/document/types`, documentType);
  }

  /** PUT /document/types */
  updateDocumentType(documentType: DocumentType): Observable<ModelValidation> {
    return this.http.put<ModelValidation>(`${this.baseUrl}/document/types`, documentType);
  }

  /** DELETE /document/types/{documentTypeId} */
  deleteDocumentType(documentTypeId: number): Observable<ModelValidation> {
    return this.http.delete<ModelValidation>(`${this.baseUrl}/document/types/${documentTypeId}`);
  }

  /** GET /document/types/user/required */
  getRequiredUserDocumentTypes(): Observable<ModelValidation> {
    return this.http.get<ModelValidation>(`${this.baseUrl}/document/types/user/required`);
  }

  /** GET /document/types/project/required */
  getRequiredProjectDocumentTypes(): Observable<ModelValidation> {
    return this.http.get<ModelValidation>(`${this.baseUrl}/document/types/project/required`);
  }

  /** GET /document/types/company/required */
  getRequiredCompanyDocumentTypes(): Observable<ModelValidation> {
    return this.http.get<ModelValidation>(`${this.baseUrl}/document/types/company/required`);
  }

  // User Documents

  /** GET /document/user/{userId} */
  getUserDocuments(userId: number): Observable<ModelValidation> {
    return this.http.get<ModelValidation>(`${this.baseUrl}/document/user/${userId}`);
  }

  /** POST /document/user/{userId} */
  saveUserDocument(userId: number, document: DocumentData): Observable<ModelValidation> {
    return this.http.post<ModelValidation>(`${this.baseUrl}/document/user/${userId}`, document);
  }

  /** DELETE /document/user/{userId}/{documentId} */
  deleteUserDocument(userId: number, documentId: number): Observable<ModelValidation> {
    return this.http.delete<ModelValidation>(`${this.baseUrl}/document/user/${userId}/${documentId}`);
  }

  // Project Documents

  /** GET /document/project/{projectId} */
  getProjectDocuments(projectId: number): Observable<ModelValidation> {
    return this.http.get<ModelValidation>(`${this.baseUrl}/document/project/${projectId}`);
  }

  /** POST /document/project/{projectId} */
  saveProjectDocument(projectId: number, document: DocumentData): Observable<ModelValidation> {
    return this.http.post<ModelValidation>(`${this.baseUrl}/document/project/${projectId}`, document);
  }

  /** DELETE /document/project/{projectId}/{documentId} */
  deleteProjectDocument(projectId: number, documentId: number): Observable<ModelValidation> {
    return this.http.delete<ModelValidation>(`${this.baseUrl}/document/project/${projectId}/${documentId}`);
  }

  // Company Documents

  /** GET /document/company/{companyId} */
  getCompanyDocuments(companyId: number): Observable<ModelValidation> {
    return this.http.get<ModelValidation>(`${this.baseUrl}/document/company/${companyId}`);
  }

  /** POST /document/company/{companyId} */
  saveCompanyDocument(companyId: number, document: DocumentData): Observable<ModelValidation> {
    return this.http.post<ModelValidation>(`${this.baseUrl}/document/company/${companyId}`, document);
  }

  /** DELETE /document/company/{companyId}/{documentId} */
  deleteCompanyDocument(companyId: number, documentId: number): Observable<ModelValidation> {
    return this.http.delete<ModelValidation>(`${this.baseUrl}/document/company/${companyId}/${documentId}`);
  }
}
